import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MoveHorizontal } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { REALISATIONS } from "@/data/realisations";

// Comparateur avant/après : glisser la poignée (souris, tactile, clavier).
export const BeforeAfterSlider = ({ before, after, alt, testId = "before-after-slider" }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  };
  const onPointerDown = (e) => { dragging.current = true; ref.current.setPointerCapture(e.pointerId); update(e.clientX); };
  const onPointerMove = (e) => { if (dragging.current) update(e.clientX); };
  const onPointerUp = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl select-none cursor-ew-resize bg-concrete touch-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid={testId}
    >
      {/* EMPLACEMENT RÉSERVÉ — remplacer par de vraies photos de chantier avec l'accord du client. */}
      <img src={after} alt={`${alt} — après (visuel d'illustration)`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" width="1200" height="900" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={before} alt={`${alt} — avant (visuel d'illustration)`} className="absolute inset-0 h-full object-cover max-w-none" style={{ width: ref.current ? ref.current.offsetWidth : "100%" }} loading="lazy" width="1200" height="900" />
      </div>
      <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-widest bg-slate/80 text-white px-3 py-1.5 rounded-full">Avant</span>
      <span className="absolute top-4 right-4 text-xs font-bold uppercase tracking-widest bg-copper text-white px-3 py-1.5 rounded-full">Après</span>
      <div className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.1)]" style={{ left: `${pos}%` }}>
        <button
          type="button"
          aria-label="Déplacer le comparateur avant/après"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          onKeyDown={(e) => { if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5)); if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5)); }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white text-slate shadow-xl flex items-center justify-center focus-visible:outline-copper"
          data-testid={`${testId}-handle`}
        >
          <MoveHorizontal size={20} />
        </button>
      </div>
    </div>
  );
};

export const BeforeAfterPreview = () => (
  <section className="section bg-concrete" data-testid="before-after-section">
    <div className="container-x">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <p className="eyebrow mb-5">Avant / Après</p>
          <h2 className="h-display text-4xl sm:text-5xl">Le résultat, <span className="text-copper">à la loupe</span></h2>
          <p className="mt-4 text-muted max-w-xl">Visuels d'illustration en attendant les photos de nos chantiers. Glissez la poignée pour comparer.</p>
        </div>
        <Link to="/realisations" className="btn-outline" data-testid="before-after-all-link">Voir les réalisations <ArrowRight size={18} /></Link>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6">
        {REALISATIONS.slice(0, 3).map((r, i) => (
          <Reveal key={r.id} delay={i * 0.1}>
            <BeforeAfterSlider before={r.before} after={r.after} alt={r.title} testId={`home-slider-${i}`} />
            <div className="mt-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="font-heading font-bold uppercase text-xl text-slate">{r.title}</h3>
                <p className="text-sm text-muted">{r.categoryLabel} · {r.place}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
